/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Component } from 'react';
import { Select, AutoComplete, Icon } from 'antd';
import { debounce, get } from 'lodash';
import { findRoutesFromZip, getRoutes } from '../services/routes.service';
import { styles } from './styles';
import { getNumAsDay, getDayAsNumber } from '../../dashboard/drive-schedule/schedule';

// Card #281 — "Allow All Routes" performance.
//
// With the checkbox ticked this control stops filtering by zip AND by day, so
// it expands the whole route table into one entry per (route, zip row, day).
// GreenHaven's 10 routes hold 5,712 zip rows x 6 days = 34,272 entries.
//
// antd 3 (rc-select 9 / rc-menu 7) has NO virtualisation: every option handed
// to the AutoComplete becomes a real <li> in the dropdown AND a mini-store
// subscriber that is re-evaluated on every hover/arrow key. On top of that,
// rc-select rebuilds its whole optionsInfo map from the option children on
// EVERY render of the surrounding order form (~42ms per keystroke at 34k).
// That is what locks the browser up, not the fetch (the ~1MB /api/routes
// payload is gzipped at level 9 by the server).
//
// The fix is to only ever hand the dropdown a bounded slice. The full list is
// kept in `routesAll` and stays completely reachable through the existing
// type-to-filter search, so every route/zip is still selectable — the control
// still allows all routes, it just stops rendering all of them at once.
const MAX_VISIBLE_OPTIONS = 200;

export default class RouteSearch extends Component {
  state = {
    error: null,
    loading: true,
    routesAll: [],
    routes: [],
    utilRoutes: [],
    value: undefined,
    selectedOption: undefined,
    options: [],
  };
  componentDidMount = async () => {
    this.retrieveZip(true);
  };
  retrieveZip = async (isInitial = false) => {
    try {
      this.setState({ loading: true });

      const opts = {};

      const utilRoutes = isInitial ? await findRoutesFromZip('00000', opts) : this.state.utilRoutes;
      const routesAll = this.props.showAll ? await getRoutes({}, opts) : await findRoutesFromZip(this.props.zip, opts);

      const routes = this.generateOptions([
        ...routesAll,
        ...utilRoutes,
      ]);

      this.setState({
        routesAll: routes,
        utilRoutes,
        routes,
        loading: false,
      }, this.createOptions);
    } catch (err) {
      console.error(err);
      this.setState({ loading: false });
    }
  }
  componentDidUpdate = (prevProps) => {
    if(this.props.zip !== prevProps.zip || this.props.showAll !== prevProps.showAll || this.props.date !== prevProps.date) {
      this.retrieveZip();
      return;
    }

    // The order form can hand us the saved route after the option list was
    // already built (setFieldsOriginal runs independently of our fetch).
    if (this.props.value !== prevProps.value) {
      this.ensureSelectedOption();
    }
  }
  generateOptions = (routes) => {
    let retRoutes = [];
    // A Set, not an object read through lodash `get`. This is a pure perf
    // change — `get(keys, value)` deduped correctly, but it pushed a ~150-char
    // JSON string through lodash's path machinery on every single row.
    const keys = new Set();

    const isShowAll = this.props.showAll;
    const dateDay = this.props.date ? getNumAsDay(getDayAsNumber(this.props.date)) : null;

    routes.map(r => {
      const rows = get(r, 'data.rows', []);
      rows.map((row, i) => {
        const isUtil = get(row, 'zipCode') === '00000';
        const matchesZip = this.props.showAll ? true : (get(row, 'zipCode') === this.props.zip || isUtil);
        if (matchesZip) {
          get(row, 'dayOfTheWeek', []).map(day => {
            if (!isShowAll && dateDay && day !== dateDay) {
              return;
            }

            const value = JSON.stringify({
              id: isUtil ? row.id : r.id,
              parent: r.id,
              name: r.name,
              city: row.city,
              zipCode: row.zipCode,
              day,
            });

            if (!keys.has(value)) {
              keys.add(value);
              retRoutes.push({
                title: `${r.name} - ${row.city}, ${row.zipCode}: ${day}`,
                value,
              })
            }
          })
        }
      })
    })
    return retRoutes;
  }
  createOptions = () => {
    const { routes, routesAll } = this.state;

    const visible = routes.slice(0, MAX_VISIBLE_OPTIONS);

    // The already-saved route has to stay in the option list even when it falls
    // outside the slice, otherwise rc-select cannot resolve its label and the
    // input shows the raw JSON value instead of "Route - City, Zip: day".
    const selected = this.getSelectedValue();
    if (selected && !visible.some(r => r.value === selected)) {
      const match = routesAll.find(r => r.value === selected);
      if (match) { visible.push(match); }
    }

    const options = visible.map(r => <Select.Option key={r.value} value={r.value}>{r.title}</Select.Option>);

    if (routes.length > MAX_VISIBLE_OPTIONS) {
      options.push(
        <Select.Option key="__truncated__" value="__truncated__" disabled>
          {`Showing ${MAX_VISIBLE_OPTIONS} of ${routes.length.toLocaleString()} matches - type to filter`}
        </Select.Option>
      );
    }

    this.setState({ options });
  }
  // The form (getFieldDecorator) feeds the selected route back in as `value`.
  // In combobox mode antd also pushes the raw typed text through the same prop,
  // so only treat it as a route when it is one of our JSON.stringify values —
  // that keeps the lookup below off the per-keystroke path.
  getSelectedValue = () => {
    const value = get(this, 'props.value');
    return typeof value === 'string' && value.charAt(0) === '{' ? value : undefined;
  }
  ensureSelectedOption = () => {
    const selected = this.getSelectedValue();
    if (!selected) { return; }
    if (this.state.options.some(o => get(o, 'props.value') === selected)) { return; }

    this.createOptions();
  }
  onChange = (value) => {
    this.setState({ value });
  }
  onSearch = debounce((input) => {
    const { routesAll } = this.state;

    this.setState({ loading: true });

    const val = input.toLowerCase();

    const routes = routesAll.filter(l => {
      const name = get(l, 'title').toLowerCase();
      if (name.indexOf(val) > -1) {
        return true;
      } else {
        return false;
      }
    })

    this.setState({
      routes,
      loading: false,
    }, this.createOptions);
  }, 500);
  selectOption = (val) => {
    const { onSelect } = this.props;
    this.setState({ 
      selectedOption: val, 
      value: val 
    }, () => onSelect && onSelect(val));
  }
  render() {
    const { loading, value, options } = this.state;

    return (
      <div css={css(styles.container)}>
        <AutoComplete
          placeholder={'Type to filter results'}
          onSearch={this.onSearch}
          onSelect={this.selectOption}
          dataSource={options}
          onChange={this.onChange}
          value={value}
          loading={loading}
          {...this.props}
        />
        {loading && <Icon css={css(styles.loader)} type="loading" />}
      </div>
    );
  }
};
